
    import * as m from '/src/paraglide/messages.js';
    import { getLocale, setLocale } from '/src/paraglide/runtime.js';
    import { mount as svelteMount, unmount as svelteUnmount } from 'svelte';
    import DemoConfigForm from '/src/lib/components/DemoConfigForm.svelte';
    import DemoDownloadButton from '/src/lib/components/DemoDownloadButton.svelte';
    import appStyles from '/src/app.css?inline';

    /* ── Copy buttons ── */
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.copy-btn');
      if (!btn) return;
      const block = document.getElementById(btn.dataset.target);
      if (!block) return;
      const copyLabel = m.btn_copy();
      const copiedLabel = m.btn_copied();
      const raw = block.innerText
        .replace(new RegExp('^(' + copyLabel + '|' + copiedLabel + ')\\n?'), '');
      navigator.clipboard.writeText(raw).then(() => {
        btn.textContent = copiedLabel;
        btn.classList.add('copied');
        setTimeout(() => {
          btn.textContent = copyLabel;
          btn.classList.remove('copied');
        }, 2000);
      });
    });

    /* ── Tabs ── */
    function initTabs(tabListId, tabContentIds) {
      const list = document.getElementById(tabListId);
      if (!list) return;
      list.addEventListener('click', (e) => {
        const trigger = e.target.closest('.tabs-trigger');
        if (!trigger) return;
        const tab = trigger.dataset.tab;
        list.querySelectorAll('.tabs-trigger').forEach((t) => {
          t.setAttribute('aria-selected', String(t === trigger));
        });
        tabContentIds.forEach((id) => {
          const el = document.getElementById(id);
          if (el) el.classList.toggle('active', id === 'tab-' + tab);
        });
      });
    }
    initTabs('mount-tabs', ['tab-auto', 'tab-manual']);
    initTabs('override-tabs', ['tab-override-auto', 'tab-override-manual']);

    /* ── Language switcher ── */
    const langSwitch = document.getElementById('lang-switch');
    if (langSwitch) {
      langSwitch.addEventListener('click', (e) => {
        const btn = e.target.closest('.lang-btn');
        if (!btn) return;
        const lang = btn.dataset.lang;
        setLocale(lang, { reload: false });
        translatePage();
        mountDownloadButton();
        if (currentConfig) {
          mountConfigForm(currentConfig);
          updateSnippets(currentConfig);
        }
        langSwitch.querySelectorAll('.lang-btn').forEach((b) => {
          b.setAttribute('aria-selected', String(b === btn));
        });
      });
    }

    /* ── i18n: translate static DOM elements ── */
    function translatePage() {
      document.documentElement.lang = getLocale();
      document.title = m.page_title();

      document.querySelectorAll('[data-i18n]').forEach((el) => {
        const key = el.getAttribute('data-i18n');
        if (key && key in m) el.textContent = m[key]();
      });

      document.querySelectorAll('[data-i18n-placeholder]').forEach((el) => {
        const key = el.getAttribute('data-i18n-placeholder');
        if (key && key in m) el.placeholder = m[key]();
      });

      const archiveEl = document.getElementById('archive-desc');
      if (archiveEl) {
        archiveEl.innerHTML = m.archive_description({
          js: '<code>quick-search.js</code>',
        });
      }

      const autoDescEl = document.getElementById('auto-desc');
      if (autoDescEl) {
        autoDescEl.innerHTML = m.auto_description({
          attr: '<code>data-attributes</code>',
        });
      }

      const overridesDescEl = document.getElementById('style-overrides-desc');
      if (overridesDescEl) {
        overridesDescEl.textContent = m.style_overrides_description();
      }

      const overrideAutoDescEl = document.getElementById('override-auto-desc');
      if (overrideAutoDescEl) {
        overrideAutoDescEl.innerHTML = m.style_overrides_auto_description({
          style: '<code>&lt;style&gt;</code>',
        });
      }

      const overrideManualDescEl = document.getElementById('override-manual-desc');
      if (overrideManualDescEl) {
        overrideManualDescEl.innerHTML = m.style_overrides_manual_description({
          opt: '<code>styleOverrides</code>',
        });
      }

      document.querySelectorAll('.copy-btn').forEach((btn) => {
        if (!btn.classList.contains('copied')) btn.textContent = m.btn_copy();
      });
    }

    /* ── Config / form logic ── */
    const STORAGE_KEY = 'qs.config';
    const defaults = {
      apiUrl: 'https://api-next.eliophot.com',
      token: '',
      lang: 'fr-FR',
      primaryColor: '',
      borderRadius: '',
      showShadow: true,
      backgroundColor: '',
      showBorder: true,
      borderColor: '',
      separateDates: false,
      showBookButton: true,
      showPricing: true,
      rooms: [],
    };

    const LANG_OPTIONS = [
      { value: 'fr-FR', label: 'fr-FR - Français' },
      { value: 'en-GB', label: 'en-GB - Anglais' },
      { value: 'de-DE', label: 'de-DE - Allemand' },
      { value: 'es-ES', label: 'es-ES - Espagnol' },
      { value: 'it-IT', label: 'it-IT - Italien' },
      { value: 'nl-NL', label: 'nl-NL - Néerlandais' },
      { value: 'pt-PT', label: 'pt-PT - Portugais' },
      { value: 'pl-PL', label: 'pl-PL - Polonais' },
      { value: 'cs-CZ', label: 'cs-CZ - Tchèque' },
      { value: 'sk-SK', label: 'sk-SK - Slovaque' },
      { value: 'hu-HU', label: 'hu-HU - Hongrois' },
      { value: 'ro-RO', label: 'ro-RO - Roumain' },
      { value: 'bg-BG', label: 'bg-BG - Bulgare' },
      { value: 'hr-HR', label: 'hr-HR - Croate' },
      { value: 'sl-SI', label: 'sl-SI - Slovène' },
      { value: 'da-DK', label: 'da-DK - Danois' },
      { value: 'sv-SE', label: 'sv-SE - Suédois' },
      { value: 'nb-NO', label: 'nb-NO - Norvégien' },
      { value: 'fi-FI', label: 'fi-FI - Finnois' },
      { value: 'el-GR', label: 'el-GR - Grec' },
      { value: 'et-EE', label: 'et-EE - Estonien' },
      { value: 'lv-LV', label: 'lv-LV - Letton' },
      { value: 'lt-LT', label: 'lt-LT - Lituanien' },
      { value: 'ga-IE', label: 'ga-IE - Irlandais' },
      { value: 'ca-ES', label: 'ca-ES - Catalan' },
    ];

    let configFormApp = null;
    let currentConfig = null;
    const configFormHostEl = document.getElementById('config-form-host');
    const configFormShadow = configFormHostEl.attachShadow({ mode: 'open' });

    function getFormLabels() {
      return {
        configTitle: m.config_title(),
        apiUrl: m.label_api_url(),
        token: m.label_token(),
        placeholderToken: m.placeholder_token(),
        lang: m.label_lang(),
        primaryColor: m.label_primary_color(),
        borderRadius: m.label_border_radius(),
        backgroundColor: m.label_background_color(),
        showShadow: m.label_show_shadow(),
        shadowOn: m.label_shadow_on(),
        shadowOff: m.label_shadow_off(),
        borderColor: m.label_border_color(),
        showBorder: m.label_show_border(),
        borderOn: m.label_border_on(),
        borderOff: m.label_border_off(),
        separateDates: m.label_separate_dates(),
        separateDatesOn: m.label_separate_dates_on(),
        separateDatesOff: m.label_separate_dates_off(),
        showBookButton: m.label_show_book_button(),
        bookButtonOn: m.label_book_button_on(),
        bookButtonOff: m.label_book_button_off(),
        showPricing: m.label_show_pricing(),
        pricingOn: m.label_pricing_on(),
        pricingOff: m.label_pricing_off(),
        reset: m.btn_reset(),
        statusReset: m.status_reset(),
        roomsTitle: m.rooms_title(),
        roomsLabel: m.rooms_label(),
        roomsIdRoom: m.rooms_id_room(),
        roomsRoomtcode: m.rooms_roomtcode(),
        roomsCatcode: m.rooms_catcode(),
        roomsAdd: m.rooms_add(),
        roomsRemove: m.rooms_remove(),
        roomsEmptyHint: m.rooms_empty_hint(),
      };
    }

    function mountConfigForm(cfg) {
      if (configFormApp) { svelteUnmount(configFormApp); configFormApp = null; }
      configFormShadow.innerHTML = '';

      const style = document.createElement('style');
      style.textContent = appStyles;
      configFormShadow.appendChild(style);

      const root = document.createElement('div');
      root.className = 'quick-search-root';
      root.style.background = 'transparent';
      configFormShadow.appendChild(root);

      currentConfig = { ...cfg };

      configFormApp = svelteMount(DemoConfigForm, {
        target: root,
        props: {
          config: cfg,
          defaults,
          langOptions: LANG_OPTIONS,
          labels: getFormLabels(),
          onapichange: async (newCfg) => {
            currentConfig = newCfg;
            saveConfig(newCfg);
            await fetchWebsiteInfo(newCfg.apiUrl, newCfg.token);
            renderAccountCard();
            updateSnippets(newCfg);
            mountWithConfig(newCfg);
            mountRooms(newCfg);
          },
          onlangchange: (newCfg) => {
            currentConfig = newCfg;
            saveConfig(newCfg);
            updateSnippets(newCfg);
            mountWithConfig(newCfg);
            mountRooms(newCfg);
          },
          onvisualchange: (newCfg) => {
            currentConfig = newCfg;
            saveConfig(newCfg);
            updateSnippets(newCfg);
            const visualOpts = {
              primaryColor: newCfg.primaryColor || '#18181b',
              borderRadius: newCfg.borderRadius,
              showShadow: newCfg.showShadow,
              backgroundColor: newCfg.backgroundColor,
              showBorder: newCfg.showBorder,
              borderColor: newCfg.borderColor,
            };
            if (currentInstance) {
              currentInstance.update(visualOpts);
            }
            for (const inst of roomInstances) {
              inst.update(visualOpts);
            }
          },
          onlayoutchange: (newCfg) => {
            currentConfig = newCfg;
            saveConfig(newCfg);
            updateSnippets(newCfg);
            mountWithConfig(newCfg);
          },
          onroomschange: (newCfg) => {
            currentConfig = newCfg;
            saveConfig(newCfg);
            updateSnippets(newCfg);
            mountRooms(newCfg);
          },
          getSiteDomain: () => websiteInfo?.domain || '',
          onreset: (newCfg) => {
            currentConfig = newCfg;
            clearConfig();
            websiteInfo = null;
            renderAccountCard();
            updateSnippets(newCfg);
            mountWithConfig(newCfg);
            mountRooms(newCfg);
          },
        },
      });
    }

    /* ── Download button (shadcn) ── */
    let dlBtnApp = null;
    const dlBtnHostEl = document.getElementById('dl-btn-host');
    const dlBtnShadow = dlBtnHostEl.attachShadow({ mode: 'open' });

    function mountDownloadButton() {
      if (dlBtnApp) { svelteUnmount(dlBtnApp); dlBtnApp = null; }
      dlBtnShadow.innerHTML = '';

      const style = document.createElement('style');
      style.textContent = appStyles;
      dlBtnShadow.appendChild(style);

      const root = document.createElement('div');
      root.className = 'quick-search-root';
      root.style.background = 'transparent';
      dlBtnShadow.appendChild(root);

      dlBtnApp = svelteMount(DemoDownloadButton, {
        target: root,
        props: {
          href: '/quick-search.zip',
          download: 'quick-search.zip',
          label: m.btn_download(),
        },
      });
    }

    let websiteInfo = null;

    async function fetchWebsiteInfo(apiUrl, token) {
      if (!apiUrl || !token) { websiteInfo = null; return; }
      try {
        const url = new URL(`${apiUrl}/items/websites`);
        url.searchParams.set('filter[user][_eq]', '$CURRENT_USER');
        url.searchParams.set('limit', '1');
        url.searchParams.set('fields', 'domain,name');
        const res = await fetch(url.toString(), {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        });
        if (!res.ok) { websiteInfo = null; return; }
        const json = await res.json();
        websiteInfo = Array.isArray(json.data) ? json.data[0] : json.data;
      } catch {
        websiteInfo = null;
      }
    }

    function renderAccountCard() {
      const el = document.getElementById('account-card');
      if (!el) return;
      if (!websiteInfo) { el.hidden = true; return; }
      el.hidden = false;
      el.querySelector('[data-name]').textContent = websiteInfo.name || '—';
      const link = el.querySelector('[data-domain-link]');
      const noLink = el.querySelector('[data-domain-nolink]');
      if (websiteInfo.domain) {
        el.querySelector('[data-domain]').textContent = websiteInfo.domain;
        link.href = `https://${websiteInfo.domain}`;
        const favicon = el.querySelector('[data-favicon]');
        favicon.src = `https://www.google.com/s2/favicons?domain=${websiteInfo.domain}&sz=32`;
        link.hidden = false;
        noLink.hidden = true;
      } else {
        link.hidden = true;
        noLink.hidden = false;
        noLink.textContent = '—';
      }
    }

    function loadConfig() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') {
            const merged = { ...defaults, ...parsed };
            merged.rooms = Array.isArray(merged.rooms) ? merged.rooms : [];
            return merged;
          }
        }
      } catch {
        /* ignore */
      }
      return { ...defaults, rooms: [] };
    }

    function saveConfig(cfg) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
    }

    function clearConfig() {
      localStorage.removeItem(STORAGE_KEY);
    }

    function esc(s) {
      return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }
    function tag(s) { return '<span class="tag">' + s + '</span>'; }
    function attr(s) { return '<span class="attr">' + s + '</span>'; }
    function str(s) { return '<span class="str">' + s + '</span>'; }
    function cmt(s) { return '<span class="cmt">' + s + '</span>'; }

    function updateSnippets(cfg) {
      const apiUrl = esc(cfg.apiUrl || defaults.apiUrl);
      const rawToken = cfg.token || m.snippet_your_token();
      const token = esc(cfg.token ? btoa(cfg.token) : rawToken);
      const lang = esc(cfg.lang || 'fr-FR');
      const color = esc(cfg.primaryColor || '#18181b');
      const radius = esc(cfg.borderRadius || '');
      const showShadow = cfg.showShadow !== false;
      const bgColor = esc(cfg.backgroundColor || '');
      const showBorder = cfg.showBorder !== false;
      const bdColor = esc(cfg.borderColor || '');
      const separateDates = cfg.separateDates === true;
      const showBookButton = cfg.showBookButton !== false;
      const showPricing = cfg.showPricing !== false;
      const copyLabel = m.btn_copy();

      const includesBlock = document.getElementById('snippet-includes');
      const autoBlock = document.getElementById('snippet-auto');
      const manualBlock = document.getElementById('snippet-manual');
      if (!includesBlock || !autoBlock || !manualBlock) return;

      includesBlock.innerHTML =
        '<button class="copy-btn" data-target="snippet-includes">' + copyLabel + '</button>' +
        cmt('&lt;!-- ' + m.snippet_comment() + ' --&gt;') + '\n' +
        tag('&lt;script') + ' ' + attr('src') + '=' + str('"./quick-search.js"') + tag('&gt;&lt;/script&gt;');

      const radiusAutoAttr = radius
        ? '\n  ' + attr('data-border-radius') + '=' + str('"' + radius + '"')
        : '';
      const shadowAutoAttr = '\n  ' + attr('data-show-shadow') + '=' + str('"' + (showShadow ? 'true' : 'false') + '"');
      const bgAutoAttr = '\n  ' + attr('data-background-color') + '=' + str('"' + (bgColor || '') + '"');
      const borderAutoAttr = '\n  ' + attr('data-show-border') + '=' + str('"' + (showBorder ? 'true' : 'false') + '"');
      const bdColorAutoAttr = '\n  ' + attr('data-border-color') + '=' + str('"' + (bdColor || '') + '"');
      const separateDatesAutoAttr = separateDates
        ? '\n  ' + attr('data-separate-dates') + '=' + str('"true"')
        : '';
      const bookButtonAutoAttr = !showBookButton
        ? '\n  ' + attr('data-show-book-button') + '=' + str('"false"')
        : '';
      const pricingAutoAttr = !showPricing
        ? '\n  ' + attr('data-show-pricing') + '=' + str('"false"')
        : '';
      autoBlock.innerHTML =
        '<button class="copy-btn" data-target="snippet-auto">' + copyLabel + '</button>' +
        tag('&lt;div') + '\n' +
        '  ' + attr('data-quick-search') + '\n' +
        '  ' + attr('data-api-url') + '=' + str('"' + apiUrl + '"') + '\n' +
        '  ' + attr('data-token') + '=' + str('"' + token + '"') + '\n' +
        '  ' + attr('data-lang') + '=' + str('"' + lang + '"') + '\n' +
        '  ' + attr('data-primary-color') + '=' + str('"' + color + '"') +
        radiusAutoAttr +
        shadowAutoAttr +
        bgAutoAttr +
        borderAutoAttr +
        bdColorAutoAttr +
        separateDatesAutoAttr +
        bookButtonAutoAttr +
        pricingAutoAttr + '\n' +
        tag('&gt;&lt;/div&gt;');

      const radiusManualLine = radius
        ? '    borderRadius:      ' + str("'" + radius + "'") + ',\n'
        : '';
      const shadowManualLine = '    showShadow:       ' + str(String(showShadow)) + ',\n';
      const bgManualLine = '    backgroundColor:  ' + str("'" + (bgColor || '') + "'") + ',\n';
      const borderManualLine = '    showBorder:       ' + str(String(showBorder)) + ',\n';
      const bdColorManualLine = '    borderColor:      ' + str("'" + (bdColor || '') + "'") + ',\n';
      const separateDatesManualLine = separateDates
        ? '    separateDates:    ' + str('true') + ',\n'
        : '';
      const bookButtonManualLine = !showBookButton
        ? '    showBookButton:   ' + str('false') + ',\n'
        : '';
      const pricingManualLine = !showPricing
        ? '    showPricing:      ' + str('false') + ',\n'
        : '';
      manualBlock.innerHTML =
        '<button class="copy-btn" data-target="snippet-manual">' + copyLabel + '</button>' +
        tag('&lt;script&gt;') + '\n' +
        '  ' + attr('const') + ' el = document.getElementById(' + str("'my-widget'") + ');\n' +
        '  ' + attr('const') + ' instance = window.QuickSearch.mount(el, {\n' +
        '    apiUrl:            ' + str("'" + apiUrl + "'") + ',\n' +
        '    token:             ' + str("'" + token + "'") + ',\n' +
        '    lang:              ' + str("'" + lang + "'") + ',\n' +
        '    primaryColor:      ' + str("'" + color + "'") + ',\n' +
        radiusManualLine +
        shadowManualLine +
        bgManualLine +
        borderManualLine +
        bdColorManualLine +
        separateDatesManualLine +
        bookButtonManualLine +
        pricingManualLine +
        '  });\n\n' +
        '  ' + cmt('// ' + m.snippet_destroy_comment()) + '\n' +
        '  ' + cmt('// instance.destroy();') + '\n' +
        tag('&lt;/script&gt;');

      updateRoomsSnippets(cfg, { apiUrl, token, lang, color, radius, showShadow, bgColor, showBorder, bdColor, copyLabel });
      updateOverrideSnippets(copyLabel);
    }

    function updateOverrideSnippets(copyLabel) {
      const autoBlock = document.getElementById('snippet-override-auto');
      const manualBlock = document.getElementById('snippet-override-manual');
      if (!autoBlock || !manualBlock) return;

      autoBlock.innerHTML =
        '<button class="copy-btn" data-target="snippet-override-auto">' + copyLabel + '</button>' +
        tag('&lt;div') + ' ' + attr('data-quick-search') + ' ' + attr('data-token') + '=' + str('"..."') + tag('&gt;') + '\n' +
        '  ' + tag('&lt;style&gt;') + '\n' +
        '    .quick-search-root { font-size: 14px; }\n' +
        '    button { letter-spacing: 0.05em; }\n' +
        '  ' + tag('&lt;/style&gt;') + '\n' +
        tag('&lt;/div&gt;');

      manualBlock.innerHTML =
        '<button class="copy-btn" data-target="snippet-override-manual">' + copyLabel + '</button>' +
        tag('&lt;script&gt;') + '\n' +
        '  ' + attr('const') + ' instance = window.QuickSearch.mount(el, {\n' +
        '    ' + cmt('// ...') + '\n' +
        '    styleOverrides: ' + str("'button { letter-spacing: 0.05em; }'") + ',\n' +
        '  });\n\n' +
        '  ' + cmt('// ' + m.style_overrides_update_comment()) + '\n' +
        '  instance.update({\n' +
        '    styleOverrides: ' + str("'.quick-search-root { padding: 2rem; }'") + ',\n' +
        '  });\n' +
        tag('&lt;/script&gt;');
    }

    function updateRoomsSnippets(cfg, parts) {
      const block = document.getElementById('rooms-install-block');
      const list = document.getElementById('rooms-snippets');
      if (!block || !list) return;

      const rooms = Array.isArray(cfg.rooms) ? cfg.rooms : [];
      const validRooms = rooms.filter((r) =>
        (r.idRoom && r.idRoom.trim()) ||
        (r.roomtcode && r.roomtcode.trim() && r.catcode && r.catcode.trim()),
      );

      if (validRooms.length === 0) {
        block.hidden = true;
        list.innerHTML = '';
        return;
      }
      block.hidden = false;

      const radiusAutoAttr = parts.radius
        ? '\n  ' + attr('data-border-radius') + '=' + str('"' + parts.radius + '"')
        : '';
      const shadowRoomAttr = '\n  ' + attr('data-show-shadow') + '=' + str('"' + (parts.showShadow ? 'true' : 'false') + '"');
      const bgRoomAttr = '\n  ' + attr('data-background-color') + '=' + str('"' + (parts.bgColor || '') + '"');
      const borderRoomAttr = '\n  ' + attr('data-show-border') + '=' + str('"' + (parts.showBorder ? 'true' : 'false') + '"');
      const bdColorRoomAttr = '\n  ' + attr('data-border-color') + '=' + str('"' + (parts.bdColor || '') + '"');

      list.innerHTML = validRooms
        .map((room, idx) => {
          const blockId = 'snippet-room-' + idx;
          const idRoomAttr = room.idRoom
            ? '\n  ' + attr('data-id-room') + '=' + str('"' + esc(room.idRoom) + '"')
            : '';
          const roomtcodeAttr = room.roomtcode
            ? '\n  ' + attr('data-roomtcode') + '=' + str('"' + esc(room.roomtcode) + '"')
            : '';
          const catcodeAttr = room.catcode
            ? '\n  ' + attr('data-catcode') + '=' + str('"' + esc(room.catcode) + '"')
            : '';
          const labelAttr = room.label
            ? '\n  ' + attr('data-label') + '=' + str('"' + esc(room.label) + '"')
            : '';

          const html =
            '<button class="copy-btn" data-target="' + blockId + '">' + parts.copyLabel + '</button>' +
            tag('&lt;div') + '\n' +
            '  ' + attr('data-quick-room-price') + '\n' +
            '  ' + attr('data-api-url') + '=' + str('"' + parts.apiUrl + '"') + '\n' +
            '  ' + attr('data-token') + '=' + str('"' + parts.token + '"') + '\n' +
            '  ' + attr('data-lang') + '=' + str('"' + parts.lang + '"') + '\n' +
            '  ' + attr('data-primary-color') + '=' + str('"' + parts.color + '"') +
            radiusAutoAttr +
            shadowRoomAttr +
            bgRoomAttr +
            borderRoomAttr +
            bdColorRoomAttr +
            idRoomAttr +
            roomtcodeAttr +
            catcodeAttr +
            labelAttr +
            '\n' + tag('&gt;&lt;/div&gt;');

          return '<div class="code-block" id="' + blockId + '">' + html + '</div>';
        })
        .join('');
    }

    let currentInstance = null;
    let roomInstances = [];

    function mountWithConfig(cfg) {
      const host = document.getElementById('widget-host');
      if (!host) return;
      if (currentInstance) {
        try {
          currentInstance.destroy();
        } catch {
          /* ignore */
        }
        currentInstance = null;
      }
      host.innerHTML = '';
      const target = document.createElement('div');
      host.appendChild(target);
      const mounter = window.QuickSearch?.mount;
      if (mounter) {
        currentInstance = mounter(target, {
          ...cfg,
          token: cfg.token ? btoa(cfg.token) : '',
        });
      }
    }

    function isValidRoom(room) {
      if (!room || typeof room !== 'object') return false;
      if (room.idRoom && room.idRoom.trim()) return true;
      if (
        room.roomtcode && room.roomtcode.trim() &&
        room.catcode && room.catcode.trim()
      ) return true;
      return false;
    }

    function mountRooms(cfg) {
      const host = document.getElementById('rooms-host');
      if (!host) return;
      for (const inst of roomInstances) {
        try { inst.destroy(); } catch { /* ignore */ }
      }
      roomInstances = [];
      host.innerHTML = '';

      const mounter = window.QuickSearch?.mountRoom;
      if (!mounter) return;

      const rooms = Array.isArray(cfg.rooms) ? cfg.rooms : [];
      const tokenB64 = cfg.token ? btoa(cfg.token) : '';
      for (const room of rooms) {
        if (!isValidRoom(room)) continue;
        const target = document.createElement('div');
        host.appendChild(target);
        const inst = mounter(target, {
          apiUrl: cfg.apiUrl,
          token: tokenB64,
          lang: cfg.lang,
          primaryColor: cfg.primaryColor || '#18181b',
          borderRadius: cfg.borderRadius,
          showShadow: cfg.showShadow,
          backgroundColor: cfg.backgroundColor,
          showBorder: cfg.showBorder,
          borderColor: cfg.borderColor,
          label: room.label || undefined,
          idRoom: room.idRoom || undefined,
          roomtcode: room.roomtcode || undefined,
          catcode: room.catcode || undefined,
        });
        roomInstances.push(inst);
      }
    }

    async function init() {
      translatePage();
      mountDownloadButton();

      const cfg = loadConfig();
      mountConfigForm(cfg);
      await fetchWebsiteInfo(cfg.apiUrl, cfg.token);
      renderAccountCard();
      updateSnippets(cfg);
      mountWithConfig(cfg);
      mountRooms(cfg);
    }

    function ensureReady() {
      if (window.QuickSearch?.mount) {
        init();
      } else {
        setTimeout(ensureReady, 30);
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', ensureReady, { once: true });
    } else {
      ensureReady();
    }
  